/**
 * New task. A title is enough; the rest is grouped by what it does for the
 * person — "helps me plan it", "helps me motivate you when you are stuck"
 * (docs/TASK_FORM_FIELD_CONTRACT_2026-09-04.md, regrouped 2026-09-05). The
 * form offers exactly what the task page's Add row offers, so nothing here
 * can only be set at creation, and nothing there is missing here.
 */
import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CheckSquare } from 'lucide-react'
import { HierarchyCreationForm } from '../components/hierarchy/HierarchyCreationForm'
import { taskFormFields } from '../config/hierarchyFormFields'
import { TaskService } from '../services/taskService'
import { useLifeAreaOptions } from '../hooks/useLifeAreas'
import { taskFormToPayload, type TaskFormData } from '../utils/taskForm'

export const CreateTask: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  // The list's quick-add hands its typed title over via "More options".
  const seededTitle = (location.state as { title?: string } | null)?.title || ''
  const [formData, setFormData] = useState<TaskFormData>({
    title: seededTitle,
    priority: 'medium',
    when: '',
    parent_type: '',
    parent_id: null,
  })
  const [formError, setFormError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const taskService = new TaskService()

  // Life areas: the canonical ones plus the person's own (owner 2026-09-05:
  // "selectable and also addable"). Whatever is picked stays offered even if
  // it was named a moment ago.
  const picked: string[] = Array.isArray(formData.life_areas) ? formData.life_areas.map(String) : []
  const { options: lifeAreaOptions, remember: rememberLifeArea } = useLifeAreaOptions(picked)
  const basicFields = useMemo(
    () => taskFormFields.basicFields.map((f) => (f.name === 'life_areas' ? { ...f, options: lifeAreaOptions } : f)),
    [lifeAreaOptions],
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    if (!String(formData.title || '').trim()) {
      setFormError('Give it a title — that is the one thing I need.')
      return
    }
    setIsLoading(true)
    try {
      const created = await taskService.createTask(taskFormToPayload(formData, 'create'))
      const newId = Number(created?.id)
      picked.forEach(rememberLifeArea)
      // "Today" is a promise about today, not just a window: pin it so it is
      // on the Today page now. Best effort — the task exists either way.
      if (formData.when === 'today' && Number.isFinite(newId)) {
        void fetch('/api/orchestrator/pin-task', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ task_id: newId, pinned: true }),
        }).catch(() => undefined)
      }
      // Fire-and-forget: resync geofences so a placed task is armed.
      void import('../services/geofenceSyncService').then(m => m.geofenceSyncService.reconcile())
      navigate(Number.isFinite(newId) ? `/tasks/${newId}` : '/tasks')
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create task')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <HierarchyCreationForm
      title="New task"
      description="A title is enough. Everything else can be added on the task itself, whenever you know it."
      icon={<CheckSquare className="h-8 w-8 text-brand" />}
      basicFields={basicFields}
      // Owner 2026-09-05: no "More". The two groups are the form; anything
      // else is added in place on the task page, the same way an existing
      // task is edited.
      advancedFields={[]}
      compactHeader
      formData={formData}
      onFormDataChange={setFormData}
      onSubmit={handleSubmit}
      submitLabel="Save"
      error={formError}
      primaryColor="indigo"
      backUrl="/tasks"
      isLoading={isLoading}
    />
  )
}
